import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../config/supabase'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'

const OneClick = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [generatedPrompt, setGeneratedPrompt] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [totalKeywords, setTotalKeywords] = useState(0)
  const [selectedStyle, setSelectedStyle] = useState('Photos')
  const [hasGeneratedToday, setHasGeneratedToday] = useState(false)
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: ''
  })
  const [remainingLimit, setRemainingLimit] = useState(null);

  const fetchProfile = async () => {
    try {
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      await fetchProfile();
      await fetchCategories();
      await fetchTotalKeywords();
      await checkRemainingLimit();
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [user]);

  const checkVIPAccess = () => {
    if (!profile?.membership || profile?.membership === 'free') {
      setNotification({
        show: true,
        message: 'เฉพาะสมาชิก VIP เท่านั้นที่สามารถใช้งานฟีเจอร์นี้ได้',
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
      return false;
    }
    return true;
  };

  const recordGeneration = async () => {
    try {
      if (!user?.id) {
        throw new Error('User not found');
      }

      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('prompt_generations')
        .insert({
          user_id: user.id,
          created_at: now.toISOString(),
          expires_at: midnight.toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Generation recorded:', data);
      return true;

    } catch (error) {
      console.error('Error in recordGeneration:', error);
      setNotification({
        show: true,
        message: 'เกิดข้อผิดพลาดในการบันทึกข้อมูล',
        type: 'error'
      });
      return false;
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true)
      console.log('Fetching categories...')
      
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('title')
      
      if (error) {
        console.error('Error fetching categories:', error)
        throw error
      }

      console.log('Categories data:', data)
      setCategories(data || [])
    } catch (error) {
      console.error('Error in fetchCategories:', error.message)
    } finally {
      setLoadingCategories(false)
    }
  }

  const fetchTotalKeywords = async () => {
    try {
      console.log('Fetching total keywords...')
      const { count, error } = await supabase
        .from('keywords')
        .select('*', { count: 'exact', head: true })

      if (error) {
        console.error('Error fetching total keywords:', error)
        throw error
      }

      console.log('Total keywords:', count)
      setTotalKeywords(count || 0)
    } catch (error) {
      console.error('Error in fetchTotalKeywords:', error.message)
      setTotalKeywords(0)
    }
  }

  const generateRandomParams = () => {
    const arOptions = ['1:1', '16:9', '9:16', '4:3', '3:4', '2:1', '1:2', '8:5', '3:2']
    const stylizeOptions = [0, 50, 100, 200, 250, 500, 750]
    const chaosOptions = [0, 10, 20, 50]

    const randomAr = arOptions[Math.floor(Math.random() * arOptions.length)]
    const randomStylize = stylizeOptions[Math.floor(Math.random() * stylizeOptions.length)]
    const randomChaos = chaosOptions[Math.floor(Math.random() * chaosOptions.length)]

    return {
      ar: randomAr,
      stylize: randomStylize,
      chaos: randomChaos
    }
  }

  const getStyleText = (style) => {
    switch (style) {
      case 'Photos':
        return 'Realistic Photo'
      case 'Ilustrations':
        return 'Vector Ilustrations'
      default:
        return style
    }
  }

  const generateRandomPrompt = async () => {
    if (!checkVIPAccess()) return;
    
    const limit = await checkRemainingLimit();
    if (limit.remaining <= 0) {
      setNotification({
        show: true,
        message: 'คุณได้ใช้สิทธิ์การสร้าง Prompt วันนี้ไปแล้ว กรุณารอถึงวันพรุ่งนี้',
        type: 'error'
      });
      setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 3000);
      return;
    }

    setIsLoading(true);
    try {
      const recorded = await recordGeneration()
      if (!recorded) {
        throw new Error('ไม่สามารถบันทึกการสร้าง Prompt ได้')
      }

      console.log('Generating random prompt...')
      const { data, error } = await supabase
        .from('keywords')
        .select(`
          keyword,
          categories (
            id,
            title
          )
        `)
        .limit(400)
      
      if (error) {
        console.error('Error generating random prompt:', error)
        throw error
      }

      console.log('Random keywords data:', data)
      
      if (data && data.length > 0) {
        const styleText = getStyleText(selectedStyle)

        const prompts = data.map(item => 
          `${item.keyword}, ${styleText} --ar ${generateRandomParams().ar} --s ${generateRandomParams().stylize} --c ${generateRandomParams().chaos} --v 6.1`
        ).join('\n')

        const randomItem = data[Math.floor(Math.random() * data.length)]
        const params = generateRandomParams()
        const generatedPrompt = `${randomItem.keyword}, ${styleText} --ar ${params.ar} --s ${params.stylize} --c ${params.chaos} --v 6.1`

        const blob = new Blob([prompts], { type: 'text/plain;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `1Click-400.txt`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        setGeneratedPrompt(generatedPrompt)
      }

      setRemainingLimit(0);
      setHasGeneratedToday(true);

    } catch (error) {
      console.error('Error in generateRandomPrompt:', error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const generatePromptByCategory = async () => {
    try {
      const { data: category, error } = await supabase
        .from('categories')
        .select('id, title')
        .eq('id', selectedCategory)
        .single();

      if (error) throw error;
      
      if (!category) {
        throw new Error('ไม่พบข้อมูลหมวดหมู่');
      }

      // ดึงคีย์เวิร์ดตามหมวดหมู่
      const { data: keywords, error: keywordsError } = await supabase
        .from('keywords')
        .select('keyword')
        .eq('category_id', category.id);

      if (keywordsError) throw keywordsError;

      // สร้าง prompts array
      let prompts = []; // ประกาศเป็น array เปล่า
      
      // สร้าง prompts 400 รายการ
      for (let i = 0; i < 400; i++) {
        const params = generateRandomParams();
        const randomKeyword = keywords[Math.floor(Math.random() * keywords.length)].keyword;
        const styleText = selectedStyle === 'Photos' ? 'Realistic Photos' : 'Vector Illustrations';
        const prompt = `${randomKeyword}, ${styleText} --ar ${params.ar} --stylize ${params.stylize} --chaos ${params.chaos} --v 6.1`;
        prompts.push(prompt);
      }

      // สร้างชื่อไฟล์
      const today = new Date();
      const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
      
      // ทำความสะอาดชื่อหมวดหมู่
      const cleanedCategoryName = category.title
        .toLowerCase()
        .replace(/&/g, '-and-')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      
      const fileName = `1click-${cleanedCategoryName}-${formattedDate}.txt`;

      // สร้างและดาวน์โหลดไฟล์
      const fileContent = prompts.join('\n'); // ใช้ join กับ array
      const blob = new Blob([fileContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // บันทึกการใช้งาน
      const recorded = await recordGeneration();
      if (!recorded) {
        throw new Error('ไม่สามารถบันทึกการสร้าง Prompt ได้');
      }

      setRemainingLimit(0);
      setHasGeneratedToday(true);

      setNotification({
        show: true,
        message: 'ดาวน์โหลด Prompts สำเร็จ',
        type: 'success'
      });

    } catch (error) {
      console.error('Error:', error);
      setNotification({
        show: true,
        message: error.message || 'ไม่สามารถสร้าง Prompt ได้',
        type: 'error'
      });
    }
  };

  const checkTodayGeneration = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data, error } = await supabase
        .from('prompt_generations')
        .select('created_at')
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString())
        .limit(1);
      
      if (error) throw error;
      
      setHasGeneratedToday(data && data.length > 0);
      if (data && data.length > 0) {
        setRemainingLimit(0);
      }
    } catch (error) {
      console.error('Error checking today generation:', error);
    }
  };

  const checkRemainingLimit = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data: usedToday, error: countError } = await supabase
        .from('prompt_generations')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());
      
      if (countError) throw countError;

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('daily_limit')
        .eq('id', user.id)
        .single();
      
      if (profileError) throw profileError;
      
      const used = usedToday?.length || 0;
      const limit = 1;
      const remaining = Math.max(0, limit - used);
      
      setRemainingLimit(remaining);
      
      return {
        used,
        limit,
        remaining
      };
    } catch (error) {
      console.error('Error checking remaining limit:', error);
      return {
        used: 0,
        limit: 1,
        remaining: 0
      };
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>1 Click Prompt</title>
      </Helmet>
      <main className="flex-grow">
        <div className="max-w-4xl mx-auto p-6">
          {profile?.membership && profile.membership !== 'free' ? (
            <>
              <h1 className="text-3xl font-bold text-center mb-8">1 Click Prompt</h1>

              <div className="mb-6 text-center">
                <div className={`inline-flex items-center px-4 py-2 rounded-full ${
                  remainingLimit === 0 ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'
                }`}>
                  <span className={`w-2 h-2 rounded-full mr-2 ${
                    remainingLimit === 0 ? 'bg-red-400' : 'bg-green-400'
                  }`}></span>
                  {remainingLimit === 0 
                    ? 'คุณได้ใช้สิทธิ์การสร้าง Prompt วันนี้ครบแล้ว' 
                    : `คุณสามารถสร้าง Prompt ได้อีก ${remainingLimit} ครั้งในวันนี้`}
                </div>
              </div>

              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Random Prompt Section */}
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                    <h2 className="text-xl font-semibold mb-4">สร้าง Prompt แบบสุ่ม</h2>
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => setSelectedStyle('Photos')}
                        className={`flex-1 py-2 px-4 rounded-lg ${
                          selectedStyle === 'Photos'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Photos
                      </button>
                      <button
                        onClick={() => setSelectedStyle('Ilustrations')}
                        className={`flex-1 py-2 px-4 rounded-lg ${
                          selectedStyle === 'Ilustrations'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Ilustrations
                      </button>
                    </div>
                    <button
                      onClick={generateRandomPrompt}
                      disabled={isLoading || loadingCategories}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 disabled:opacity-50"
                    >
                      {isLoading ? 'กำลังสร้าง...' : 'สร้าง Prompt แบบสุ่ม'}
                    </button>
                  </div>

                  {/* Category Prompt Section */}
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                    <h2 className="text-xl font-semibold mb-4">สร้าง Prompt ตามหมวดหมู่</h2>
                    <div className="flex gap-2 mb-4">
                      <button
                        onClick={() => setSelectedStyle('Photos')}
                        className={`flex-1 py-2 px-4 rounded-lg ${
                          selectedStyle === 'Photos'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Photos
                      </button>
                      <button
                        onClick={() => setSelectedStyle('Ilustrations')}
                        className={`flex-1 py-2 px-4 rounded-lg ${
                          selectedStyle === 'Ilustrations'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Ilustrations
                      </button>
                    </div>
                    <button
                      onClick={generatePromptByCategory}
                      disabled={isLoading || !selectedCategory || loadingCategories}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 disabled:opacity-50 mb-4"
                    >
                      {isLoading ? 'กำลังสร้าง...' : 'สร้าง Prompt ตามหมวดหมู่'}
                    </button>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      disabled={loadingCategories}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg py-2 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                    >
                      <option value="">เลือกหมวดหมู่</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.title}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* คำแนะนำการใช้งานแบบ Steps */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h2 className="text-xl font-semibold mb-6 text-center">วิธีการใช้งาน</h2>
                  <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Step 1 */}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center font-bold text-xl">
                            1
                          </div>
                          <h3 className="text-lg font-medium text-purple-400">เลือกประเภทภาพ</h3>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4 flex-grow">
                          <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50">
                              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium">Photos</div>
                                <div className="text-sm text-gray-400">สร้างภาพถ่ายสมจริง</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50">
                              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium">Illustrations</div>
                                <div className="text-sm text-gray-400">สร้างภาพวาดและกราฟิก</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Step 2 */}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center font-bold text-xl">
                            2
                          </div>
                          <h3 className="text-lg font-medium text-purple-400">เลือกวิธีการสร้าง</h3>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4 flex-grow">
                          <div className="space-y-3">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50">
                              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium">สร้างแบบสุ่ม</div>
                                <div className="text-sm text-gray-400">สุ่มจากทุกหมวดหมู่</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50">
                              <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                                <svg className="w-4 h-4 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium">สร้างตามหมวดหมู่</div>
                                <div className="text-sm text-gray-400">เลือกหมวดหมู่ที่ต้องการ</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Step 3 */}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center font-bold text-xl">
                            3
                          </div>
                          <h3 className="text-lg font-medium text-purple-400">กดสร้าง Prompt</h3>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4 flex-grow">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                              <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium">ดาวน์โหลดอัตโนมัติ</div>
                              <div className="text-sm text-gray-400">
                                <span className="font-medium text-purple-400">ระบบจะสร้างไฟล์ .txt ที่ประอบด้วย:</span>
                                <ul className="mt-0.5 ml-4 space-y-0.5">
                                  <li>• Prompt จำนวน 400</li>
                                  <li>• ดาวน์โหลดอัตโนมัติทันที</li>
                                  <li>• <span className="text-purple-300 font-medium border-b border-purple-500">เป็นจำนวนที่เหมาะต่อวัน</span></li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-red-500 mb-4">
                VIP Access Only
              </h2>
              <p className="text-gray-400">
                เฉพาะสมาชิก VIP เท่านั้นที่สามารถใช้งานฟีเจอร์นี้ได้
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default OneClick 